import React, { FC, useCallback, useMemo, useState } from 'react';
import { cx } from '@linaria/core';
import { toJS } from 'mobx';
import { modelsMST } from 'modules/Models/listingStore/mst-types';
import { workflowListMST } from 'modules/Workflows/listingStore/mst-types';
import { flex } from 'styles/utils';
import { Card } from 'modules/Home/cards/Card';
import { v4 as uuidv4 } from 'uuid';
import { IconArrowRight, IconChevronRight } from 'components/Icons';
import { queryKeys } from 'utils/react-query';
import { IconButton } from 'components/Button/IconButton';
import { featuredApi } from 'modules/Featured/components/FeaturedItems/apiEffects';
import { Popover, PopoverGroup, PopoverAction, SegmentTitle, usePopoverState } from 'components/Popover';
import { SorterItems, ItemValue } from 'modules/Listings/components/Sorter/SorterItems';
import { IconSortDown } from 'components/Icons/IconSortDown';
import { HorizontalLayout } from 'modules/Home/layout/HorizontalLayout';
import { FeaturedModel } from 'modules/Explore/types';
import {
  cardCls,
  wrapper,
  heading,
  chevronIcon,
  sorterWrapper,
  headingContainer,
  chevronIconButton,
  loadMoreContainer,
  chevronIconTooltip,
} from 'modules/Explore/styles';
import { useRouter } from 'next/router';
import { Button } from 'components/Button';
import { Spinner } from 'components/Spinner';
import { useQuery } from 'react-query';
import {
  navigateToPlayground,
  navigateToCommunityPage,
  sortFunctions,
  appSortCriteria,
  SortKey,
  SortOption,
  viewAllCategoryModels,
  viewAllWorkflows,
  mergeCardsWithConfigAndIcons,
} from '../utils';

type EntityCategory = {
  [key: string]: {
    type: 'model' | 'workflow';
    useCase?: string;
    categoryHeading: string;
    viewAllLabel?: string;
    viewMoreAction?: (router: ReturnType<typeof useRouter>, category?: string) => void;
    fetchAction: () => Promise<CF.API.Models.Model[] | CF.API.Workflows.Workflow[]>;
  };
};

const entityCategoryMap: EntityCategory = {
  featured: {
    type: 'model',
    categoryHeading: '🔥 Trending Models',
    fetchAction: featuredApi.listFeaturedModels,
  },
  llm: {
    type: 'model',
    useCase: 'llm',
    categoryHeading: 'Large Language Models',
    viewAllLabel: 'See all Large Language Models',
    viewMoreAction: viewAllCategoryModels,
    fetchAction: featuredApi.listLlmModels,
  },
  vlm: {
    type: 'model',
    useCase: 'visual-question-answering',
    categoryHeading: 'Vision Language Models',
    viewAllLabel: 'See all Vision Language Models',
    viewMoreAction: viewAllCategoryModels,
    fetchAction: featuredApi.listVlmModels,
  },
  workflows: {
    type: 'workflow',
    categoryHeading: 'Popular Workflows',
    viewAllLabel: 'See all Workflows',
    viewMoreAction: viewAllWorkflows,
    fetchAction: featuredApi.listFeaturedWorkflows,
  },
};

const EntityListLayout = React.memo(
  ({
    featured,
    entities,
    entityType,
    router,
  }: {
    featured?: boolean;
    entities: FeaturedModel[] | CF.API.Workflows.Workflow[];
    entityType: 'model' | 'workflow';
    router: ReturnType<typeof useRouter>;
  }) => {
    // TODO: We don't support workflows in the playground yet:
    const isPlaygroundCompatible = entityType === 'model';
    const shouldShowIcon = entityType === 'model' && featured;

    // ❌ No entities found, nothing to show
    if (!entities.length) return null;

    return (
      <HorizontalLayout>
        {entities.map((entity) => (
          <Card
            key={uuidv4()}
            icon={shouldShowIcon && 'iconComponent' in entity && entity.iconComponent && <entity.iconComponent size={36} />}
            imageUrl={entity?.image?.url || ''}
            title={entity.id}
            body={entity.description}
            cardClassName={cardCls}
            footer={
              <>
                <button
                  className={cx(flex.init, flex.gap.half, flex.justifyContent.center, flex.alignItems.center, 'link', 'details-link')}
                  onClick={() => navigateToCommunityPage(entity, entityType, router)}
                  type="button"
                >
                  VIEW DETAILS
                  <IconArrowRight size={16} />
                </button>
                {isPlaygroundCompatible && (
                  <button
                    className={cx(
                      flex.init,
                      flex.gap.half,
                      flex.justifyContent.center,
                      flex.alignItems.center,
                      'link',
                      'details-link',
                      'playground-link',
                    )}
                    onClick={() => navigateToPlayground(entity as CF.API.Models.Model, router)}
                    type="button"
                  >
                    TEST IN PLAYGROUND
                    <IconArrowRight size={16} />
                  </button>
                )}
              </>
            }
          />
        ))}
      </HorizontalLayout>
    );
  },
);

EntityListLayout.displayName = 'EntityListLayout'; // For React DevTools

type EntityCategoryWithFiltersProps = {
  category: 'featured' | 'llm' | 'vlm' | 'workflows';
  resultsToShow?: number;
  initialData?: CF.API.Models.Model[] | CF.API.Workflows.Workflow[];
};

export const EntityCategoryWithFilters: FC<EntityCategoryWithFiltersProps> = ({ category, resultsToShow = 9, initialData }) => {
  const router = useRouter();
  const [sortBy, setSortBy] = useState<SortOption<SortKey>>(appSortCriteria[0]);
  const popover = usePopoverState({ placement: 'bottom-end' });

  const categoryConfig = entityCategoryMap[category];
  if (!categoryConfig) return null;

  const { categoryHeading, viewAllLabel, viewMoreAction, fetchAction } = categoryConfig;

  if (!categoryConfig) return null;

  const { data: categoryEntities, isLoading } = useQuery([queryKeys.Models, category], fetchAction, {
    initialData,
  });
  const isFeatured = category === 'featured';

  const sortedEntities = useMemo(() => {
    // No data or something went wrong:
    if (!categoryEntities) {
      return [];
    }

    // Pull out the response of items:
    const fetchedEntityList = categoryEntities;

    // Process it in the legacy MST store matching the entity type:
    const entityList =
      categoryConfig.type === 'model'
        ? modelsMST.create(fetchedEntityList as CF.API.Models.Model[])
        : workflowListMST.create(fetchedEntityList as CF.API.Workflows.Workflow[]);
    // Enrich the data with hard coded config and icons:
    const enrichedEntityList = mergeCardsWithConfigAndIcons(toJS(entityList));

    // Handle the sorting...
    const entityListClone = [...enrichedEntityList];

    // Skip sorting if the category is featured:
    if (isFeatured) {
      return entityListClone;
    }

    const sortFn = sortFunctions[sortBy.value];
    return sortFn ? entityListClone.sort(sortFn) : entityListClone;
  }, [categoryEntities, sortBy.value]);

  const sortByHandler = useCallback(
    ({ value }: { value: ItemValue }) => {
      const foundEntity = appSortCriteria.find((item) => item.value === value);
      if (foundEntity) {
        setSortBy(foundEntity);
      }
      popover.hide();
    },
    [setSortBy, popover],
  );

  const sortByLabel = appSortCriteria.find(({ value }) => value === sortBy.value)?.name;

  // ❌ No entities found, hide the category:
  if (!sortedEntities.length) return null;

  return (
    <div className={wrapper}>
      <div className={headingContainer}>
        <h2 className={heading}>{categoryHeading}</h2>
        {!isFeatured && (
          <>
            <IconButton
              size={20}
              variant="link"
              Icon={IconChevronRight}
              iconClassName={chevronIcon}
              className={chevronIconButton}
              aria-label="autocomplete-button"
              tooltipClassName={chevronIconTooltip}
              onClick={() => viewMoreAction && viewMoreAction(router, categoryConfig.useCase)}
            />
            <div className={sorterWrapper}>
              <PopoverAction popover={popover} style={{ flex: 1, display: 'flex' }}>
                <div className={cx('title', 'container')}>
                  <span className="sortByLabel">{sortByLabel}</span>
                  <IconSortDown size={8} color="#808080" />
                </div>
              </PopoverAction>
              <Popover popover={popover} aria-label="Sort By">
                <PopoverGroup style={{ borderTop: 0, paddingTop: 0 }}>
                  <SegmentTitle>SORT BY</SegmentTitle>
                  <SorterItems items={appSortCriteria} selectedValue={sortBy.value} onChange={sortByHandler} />
                </PopoverGroup>
              </Popover>
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spinner />
        </div>
      ) : (
        <EntityListLayout router={router} featured={isFeatured} entityType={categoryConfig.type} entities={sortedEntities.slice(0, resultsToShow)} />
      )}

      {categoryConfig.viewAllLabel && (
        <div className={loadMoreContainer}>
          <Button
            size="md"
            variant="tertiary"
            onClick={() => viewMoreAction && viewMoreAction(router, categoryConfig.useCase)}
            className="load-more-btn"
          >
            {viewAllLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
